import { useState, useCallback, useRef, useMemo, ReactNode, useEffect } from 'react';
import { SandboxRunnerContext } from './SandboxRunnerContext';
import type {
  SandboxSystemsInput,
  ClientRecord,
  ClientRecordWithDOM,
  PlaygroundStepView,
  RunnerStatus,
  TestRunResult,
  TestProgressState,
  SuiteProgressItem,
  SuiteResult,
  TestResult,
  TestItemState,
  CollectedTestSuite,
  RerunTarget,
} from './types';
import { DEFAULT_CLIENT_SETTINGS } from './types';
import { isSyntheticSpecPath, stripSyntheticSuites } from './stripSyntheticSpec';
import { PLAYGROUND_CONFIG_PATH, listProjectPlaygrounds } from './projectPlaygrounds';
import { sandboxExternals } from './sandboxExternals';
import { BootCancelledError, BootTimeoutError, LIVE_BOOT_TIMEOUT_MS, bootWithin } from './bootWithin';
import {
  dslStepViews,
  dslTestKey,
  playgroundLoadErrorSuite,
  playgroundProgressItem,
  playgroundSuiteResult,
  playgroundTestResult,
  withDslSteps,
  type PlaygroundTestRun,
} from './playgroundResults';
import { ConsoleCapture, gateBudgetMs, type PlaygroundTestProgress, type PlaygroundTestSuiteResult } from '@gotti/playground/browser';
import type { GboxClientSettings } from '@gotti/sandbox-shared';
import {
  initializeRuntime,
  bootRuntimeFromProject,
  addClientToRuntime,
  removeClientFromRuntime,
  stopRuntime,
  setBootAssetSeamOverride,
  getLastBootAssetManager,
  RuntimeState,
} from './SandboxRuntime';
import { installTestInputDriver } from './testInputDriver';
import { createPreviewQueue, runWithPreviewSuspended, type PreviewQueue } from './previewTransitions';
import {
  resetTests,
  setCurrentTestFile,
  runCollectedTests,
  describe as testDescribe,
  it as testIt,
  test as testTest,
  expect as testExpect,
  beforeEach as testBeforeEach,
  afterEach as testAfterEach,
  beforeAll as testBeforeAll,
  afterAll as testAfterAll,
  CoverageTracker,
  type CoverageReport,
  type TestProgressEvent,
  type TestAssetRef,
  defineSandboxTest as coreDefineSandboxTest,
  setSandboxGameBooter,
  setAssetHost,
  instrumentContext,
  collectRegisteredTests,
  treePathOfV1Path,
  v1PathOfTreePath,
  type ProjectTree,
} from '@gotti/sandbox-core';
import { compileAndLoad } from '@gotti/sandbox-core';
import { loadSpecFile, runOnClock, runsOnPlayground, specLoadErrorSuite, type PlaygroundBootOptions } from '@gotti/sandbox-test';
import { bootRealRuntime } from './bootRealRuntime';
import { createTestAssetSource } from '../assets/createTestAssetSource';
import { createTestAssetHost, type TestAssetHost } from '../assets/testAssetHost';
import { checkedAssetPreloadPaths } from '../assets/browserAssetSource';
import * as GottiClient from '@gotti/client';
import * as GottiServer from '@gotti/server';
import * as GottiCore from '@gotti/core';
import * as BrowserTestUtils from '@gotti/test-utils/harness-browser';
import * as TestUtils from '@gotti/test-utils';
import { mountTestVisualization, clearTestVisualization } from './testVisualization';

// The defineSandboxTest input driver (press / click / type) and the focus-scoped key routing it relies on,
// installed once at load — see testInputDriver.ts. The parity harness installs the same driver.
installTestInputDriver();

// ── Test visualization ──────────────────────────────────────────────────────
// `mountTestVisualization` / `clearTestVisualization` live in ./testVisualization
// (extracted so they're unit-testable). They mount a `clients: N` test's per-
// client surfaces using the SAME split-screen layout + CSS classes as "+ Add
// Client", and reflow when a test disconnects a client.

/** One test's multi-client observation, broadcast beside a run's result (never inside the compared payload). */
type MultiClientObservation = { suiteName: string; testName: string; file?: string; clients: number; status: string };

/**
 * Posts a finished run to the page (and a parent frame) as `test-results`. The payload leaves out the orchestrator's
 * synthetic boot-smoke spec, so in-page observers (Tests panel + eval gate) report the SAME shipped count the
 * orchestrator does — the RETURNED result stays raw, since it feeds postTestResults → the backend's own bootSmokeMerge
 * strip + the checkpoint cassette, which must remain byte-identical. `multiClient` is an observation-only sidecar so an
 * eval can assert a clients>=2 test PASSED.
 */
function broadcastTestResults(result: TestRunResult, multiClient: MultiClientObservation[]): void {
  try {
    window.postMessage({ source: 'gotti:sandbox', type: 'test-results', payload: stripSyntheticSuites(result), multiClient }, '*');
  } catch { /* swallow — not critical */ }
}

/** A run's totals, counted from its suites as the engine counts them: an `error` is a failure. */
function withTotals(result: Omit<TestRunResult, 'totalTests' | 'totalPassed' | 'totalFailed' | 'totalSkipped'>): TestRunResult {
  const tests = result.suites.flatMap((suite) => suite.tests);
  return {
    ...result,
    totalTests: tests.length,
    totalPassed: tests.filter((test) => test.status === 'passed').length,
    totalFailed: tests.filter((test) => test.status === 'failed' || test.status === 'error').length,
    totalSkipped: tests.filter((test) => test.status === 'skipped').length,
  };
}

/** A suite's or target's file, compared without the engine's leading `/`. */
const sameFile = (a: string | undefined, b: string | undefined) => a !== undefined && b !== undefined && a.replace(/^\/+/, '') === b.replace(/^\/+/, '');

/**
 * The results on screen with the one test a rerun ran in place of its row — matched by file, suite and name, since two
 * spec files may declare one suite name — or appended to its suite, or as a new suite, when they lack it. A suite of a
 * file the project no longer has (another project's, an AI run of files outside it) is dropped. A test the rerun could
 * not run is never left at its old status: its row says why — its file's load error, or that the file no longer
 * defines it. Says which test it was (`rerun`).
 */
function mergeRerun(
  shown: TestRunResult | null,
  ran: TestRunResult,
  target: { file: string; suiteName: string; testName: string },
  inProject: (file: string | undefined) => boolean,
): TestRunResult {
  const ranSuite = ran.suites.find((suite) => sameFile(suite.file, target.file) && suite.name === target.suiteName);
  const fresh = ranSuite?.tests.find((test) => test.name === target.testName);
  const loadError = ran.suites.filter((suite) => sameFile(suite.file, target.file)).flatMap((suite) => suite.tests).find((test) => test.error?.name === 'LoadError');
  const row: TestResult = fresh ?? {
    name: target.testName,
    suiteName: target.suiteName,
    file: target.file,
    status: 'error',
    duration: 0,
    error: loadError?.error ?? {
      name: 'RerunError',
      message: ran.cancelled
        ? `The rerun was cancelled before "${target.testName}" ran.`
        : `"${target.testName}" did not run: ${target.file} no longer defines it.`,
    },
  };
  const recount = (suite: SuiteResult): SuiteResult => ({
    ...suite,
    passed: suite.tests.filter((test) => test.status === 'passed').length,
    failed: suite.tests.filter((test) => test.status === 'failed' || test.status === 'error').length,
    skipped: suite.tests.filter((test) => test.status === 'skipped').length,
  });
  const base = shown ? shown.suites.filter((suite) => inProject(suite.file)) : [];
  const isTargetSuite = (suite: SuiteResult) => sameFile(suite.file, target.file) && suite.name === target.suiteName;
  const suites = base.some(isTargetSuite)
    ? base.map((suite) => {
        if (!isTargetSuite(suite)) return suite;
        const has = suite.tests.some((test) => test.name === target.testName);
        return recount({ ...suite, tests: has ? suite.tests.map((test) => (test.name === target.testName ? row : test)) : [...suite.tests, row] });
      })
    : [...base, recount({ name: target.suiteName, file: ranSuite?.file ?? target.file, tests: [row], passed: 0, failed: 0, skipped: 0, duration: row.duration ?? 0 })];
  return withTotals({
    suites,
    duration: ran.duration,
    ...(shown?.coverage ? { coverage: shown.coverage } : {}),
    ...(ran.cancelled ? { cancelled: true as const } : {}),
    rerun: { suiteName: target.suiteName, testName: target.testName },
  });
}

/** The engine's status as a progress row's state. */
const itemStateOf = (status: TestResult['status']): TestItemState =>
  status === 'passed' || status === 'failed' || status === 'skipped' ? status : 'error';

/**
 * A finished run as the progress tree the results panel counts: every suite and test of the result, each once — a load
 * error included, a test a cancelled run never reached left out, a rerun's whole merged run — so the panel's counts
 * are the payload's.
 */
function progressFromResult(result: TestRunResult): TestProgressState {
  return {
    phase: 'complete',
    suites: result.suites.map((suite) => ({
      name: suite.name,
      state: suite.failed === 0 ? 'passed' : 'failed',
      collapsed: false,
      tests: suite.tests.map((test) => ({
        name: test.name,
        state: itemStateOf(test.status),
        ...(test.duration === undefined ? {} : { duration: test.duration }),
        ...(test.error ? { error: test.error.message } : {}),
        ...(test.playground ? { steps: test.playground.steps } : {}),
      })),
    })),
  };
}

/**
 * Forwards every console call to `capture` (as a page's console reaches the CLI) while `run` runs, the original
 * console still called: what attributes a playground step's console lines to the step that produced them.
 */
async function withConsoleCapture<T>(capture: ConsoleCapture, run: () => Promise<T>): Promise<T> {
  const types = { log: 'log', info: 'info', warn: 'warning', error: 'error', debug: 'debug' } as const;
  const originals = new Map<keyof typeof types, (...args: unknown[]) => void>();
  const text = (arg: unknown): string => {
    if (typeof arg === 'string') return arg;
    if (arg instanceof Error) return arg.message;
    try { return JSON.stringify(arg) ?? String(arg); } catch { return String(arg); }
  };
  for (const method of Object.keys(types) as Array<keyof typeof types>) {
    const original = console[method] as (...args: unknown[]) => void;
    originals.set(method, original);
    console[method] = (...args: unknown[]) => {
      capture.observe(types[method], args.map(text).join(' '));
      original.apply(console, args);
    };
  }
  try {
    return await run();
  } finally {
    for (const [method, original] of originals) console[method] = original;
  }
}

/**
 * The spec files a project runs, as the AI and the engine see them: every file of its AI view at its v1 path with a
 * leading `/` (generated files left out), the pipeline's synthetic specs left out. The project's own files only: the
 * AI writes into the tree (its root `__tests__/` included), so the agent-file mirror — the AI worker's last snapshot,
 * kept across edits and project loads — would run a stale copy over the person's edit.
 */
function projectSpecFiles(project: ProjectTree): Map<string, string> {
  const files = new Map<string, string>();
  for (const [path, content] of Object.entries(project.files)) {
    if (project.generated.has(path)) continue;
    const v1Path = v1PathOfTreePath(path);
    if (v1Path !== null && !isSyntheticSpecPath(`/${v1Path}`)) files.set(`/${v1Path}`, content);
  }
  return files;
}

const SPEC_FILE = /\.(spec|test)\.(ts|tsx)$/;

/** A signal that aborts when any of `signals` does: `AbortSignal.any`, which not every host has (jsdom does not). */
function anySignal(signals: AbortSignal[]): AbortSignal {
  const controller = new AbortController();
  for (const signal of signals) {
    if (signal.aborted) {
      controller.abort(signal.reason);
      break;
    }
    signal.addEventListener('abort', () => controller.abort(signal.reason), { once: true });
  }
  return controller.signal;
}

/**
 * `record`, made with its surface before its client existed, becomes `runtimeClient`: the id the client authenticated
 * as — on its canvas and container too — and the instance.
 */
function adoptRuntimeClient(record: ClientRecordWithDOM, runtimeClient: ClientRecord | undefined): void {
  if (!runtimeClient) return;
  record.id = runtimeClient.id;
  record.canvas.dataset.clientId = runtimeClient.id;
  record.container.dataset.clientId = runtimeClient.id;
  record.instance = runtimeClient.instance;
}

interface SandboxRunnerProviderProps {
  children: ReactNode;
}

export function SandboxRunnerProvider({ children }: SandboxRunnerProviderProps) {
  const [status, setStatus] = useState<RunnerStatus>('idle');
  const [statusText, setStatusText] = useState('Ready');
  const [isLoading, setIsLoading] = useState(false);
  const [clients, setClients] = useState<ClientRecordWithDOM[]>([]);
  const [focusedClientId, setFocusedClientId] = useState<string | null>(null);
  const [testResults, setTestResults] = useState<TestRunResult | null>(null);
  // Each `defineSandboxTest` test's steps as it last ran on the playground's runner, by `dslTestKey`: the panels show
  // them on its result (`withDslSteps`), while the results a run returns and posts stay the engine's.
  const [dslSteps, setDslSteps] = useState<ReadonlyMap<string, { id: string; steps: PlaygroundStepView[] }>>(() => new Map());
  // The same, as of now — what a run's closing progress tree is built from before the state above has rendered.
  const dslStepsRef = useRef<ReadonlyMap<string, { id: string; steps: PlaygroundStepView[] }>>(new Map());
  const shownTestResults = useMemo(() => withDslSteps(testResults, dslSteps), [testResults, dslSteps]);
  const [isRunningTests, setIsRunningTests] = useState(false);
  const [coverageReport, setCoverageReport] = useState<CoverageReport | null>(null);
  const [testProgress, setTestProgress] = useState<TestProgressState>({ suites: [], phase: 'idle' });

  // ── Agent-file mirror ──────────────────────────────────────────────────────
  // A live copy of the AI worker's file set, keyed by normalized path ("/folder
  // /file.ts"). The editor's structured `systems` (client/server/shared) cannot
  // represent the worker's flat `__tests__/*.spec.ts` specs, so without this
  // mirror the Tests panel discovers nothing and shows "No tests found" while
  // the agent is running failing specs. Fed from BOTH the per-file file:modified
  // stream and every test:request, so discovery always reflects what the AI
  // actually holds. The ref is the source of truth (read fresh by collectTests);
  // the signature only exists to re-trigger the panel's debounced discovery.
  const agentFilesRef = useRef<Map<string, string>>(new Map());
  const [agentFilesSig, setAgentFilesSig] = useState('');
  const syncAgentFiles = useCallback((files: Record<string, string>) => {
    let changed = false;
    for (const [p, content] of Object.entries(files)) {
      const key = p.startsWith('/') ? p : `/${p}`;
      if (agentFilesRef.current.get(key) !== content) {
        agentFilesRef.current.set(key, content);
        changed = true;
      }
    }
    // Bump a monotonic signature only on a real change, so the panel's
    // (compile-heavy) discovery doesn't re-run on no-op syncs.
    if (changed) setAgentFilesSig((s) => String(Number(s || '0') + 1));
  }, []);

  // AUTHORITATIVE full-snapshot sync — REPLACES the mirror with exactly the
  // worker's working-file set. Unlike `syncAgentFiles` (upsert-only), this drops
  // mirror entries absent from the snapshot, so rollbacks / deletes / renames
  // propagate and the panel can never show a stale spec the AI no longer holds.
  // Fed by the `files:sync` event, emitted on every workingFiles mutation AND
  // before every test run.
  const replaceAgentFiles = useCallback((files: Record<string, string>) => {
    const next = new Map<string, string>();
    for (const [p, content] of Object.entries(files)) {
      next.set(p.startsWith('/') ? p : `/${p}`, content);
    }
    // Detect a real change (size or any key/value differs) to avoid needless
    // (compile-heavy) re-discovery on identical snapshots.
    const prev = agentFilesRef.current;
    let changed = prev.size !== next.size;
    if (!changed) {
      for (const [k, v] of next) {
        if (prev.get(k) !== v) { changed = true; break; }
      }
    }
    agentFilesRef.current = next;
    if (changed) setAgentFilesSig((s) => String(Number(s || '0') + 1));
  }, []);

  // Ref-based progress to avoid stale closure in onProgress callback
  const progressRef = useRef<TestProgressState>({ suites: [], phase: 'idle' });
  /** The multi-client sidecar of the last spec run: a Run All broadcasts it with the whole result. */
  const lastMultiClientRef = useRef<Array<{ suiteName: string; testName: string; file?: string; clients: number; status: string }>>([]);
  /** The results on screen, whichever run made them — what a rerun's one test is merged into. */
  const shownResultsRef = useRef<TestRunResult | null>(null);
  useEffect(() => { shownResultsRef.current = testResults; }, [testResults]);
  /** The controller of the run the panel started, which Cancel aborts; null while none is running. */
  const panelRunRef = useRef<AbortController | null>(null);

  /**
   * Progress callback — updates streaming state as each test transitions.
   * Uses a ref + setState flush so the UI sees every intermediate state.
   */
  const onTestProgress = useCallback((event: TestProgressEvent) => {
    const p = progressRef.current;

    switch (event.type) {
      case 'run-start': {
        // When processing multiple test files, run-start fires per file.
        // On the first file, reset to fresh state. On subsequent files,
        // keep existing suites and continue accumulating.
        if (p.phase !== 'running') {
          const fresh: TestProgressState = { suites: [], phase: 'running' };
          progressRef.current = fresh;
          setTestProgress({ ...fresh });
        }
        // else: already running (multi-file) — don't reset
        break;
      }
      case 'suite-start': {
        const suite: SuiteProgressItem = {
          name: event.suiteName,
          state: 'running',
          tests: Array.from({ length: event.testCount }, () => ({
            name: '', state: 'pending' as const,
          })),
          collapsed: false,
        };
        p.suites = [...p.suites, suite];
        setTestProgress({ ...p, suites: [...p.suites] });
        break;
      }
      case 'test-start': {
        const suite = p.suites.find(s => s.name === event.suiteName);
        if (suite) {
          // Find next pending test slot and mark it
          const slot = suite.tests.find(t => t.state === 'pending');
          if (slot) {
            slot.name = event.testName;
            slot.state = 'running';
          }
          setTestProgress({ ...p, suites: [...p.suites] });
        }
        break;
      }
      case 'test-end': {
        const suite = p.suites.find(s => s.name === event.suiteName);
        if (suite) {
          const t = suite.tests.find(t => t.name === event.testName && t.state === 'running');
          if (t) {
            t.state = event.status === 'passed' ? 'passed'
              : event.status === 'skipped' ? 'skipped'
              : event.status === 'failed' ? 'failed' : 'error';
            // The engine's `test-end` event carries no duration (durations were
            // moved out of the compared payload into `RunMetadata` for
            // node/browser parity — see runner/types.ts), so nothing is written
            // here; `t.duration` stays undefined and the panel shows no timing.
            t.error = event.error?.message;
          }
          setTestProgress({ ...p, suites: [...p.suites] });
        }
        break;
      }
      case 'suite-end': {
        const suite = p.suites.find(s => s.name === event.suite.name);
        if (suite) {
          suite.state = event.suite.failed === 0 ? 'passed' : 'failed';
          // Remove unfilled pending slots
          suite.tests = suite.tests.filter(t => t.state !== 'pending');
          setTestProgress({ ...p, suites: [...p.suites] });
        }
        break;
      }
      case 'run-end': {
        // Per-file run-end: don't mark phase as complete yet.
        // The executeTests loop handles final phase transition after all files.
        setTestProgress({ ...p, suites: [...p.suites] });
        break;
      }
    }
  }, []);

  // Runtime state is kept in a ref since React doesn't need to re-render for it
  const runtimeStateRef = useRef<RuntimeState | null>(null);
  // Keep track of clients with their DOM elements
  const clientsWithDOMRef = useRef<ClientRecordWithDOM[]>([]);
  // The code the live preview last ran — what a test run resumes the preview with.
  const lastRunSystemsRef = useRef<SandboxSystemsInput | ProjectTree | null>(null);
  // Every live-preview transition — a run, a stop, an added or removed client, a whole test run (which suspends the
  // preview and resumes it) — goes through this queue, so two never interleave (see previewTransitions.ts).
  const previewQueueRef = useRef<PreviewQueue>(createPreviewQueue());
  const enqueuePreview = useCallback(<T,>(transition: () => Promise<T>): Promise<T> => previewQueueRef.current.enqueue(transition), []);

  // Route keyboard input to the preview client `id` only — its listeners are scoped under its input token — or, with
  // no client (or one without a token), to every live client (e.g. a single-client preview). The live highlight and
  // the input focus are one: this runs whenever the highlighted client changes, and after every boot.
  const applyPreviewFocus = useCallback((id: string | null) => {
    const token = id ? runtimeStateRef.current?.clientTokens.get(id) : undefined;
    if (token === undefined) TestUtils.clearFocusedClients();
    else TestUtils.setFocusedClients([token]);
  }, []);
  useEffect(() => {
    applyPreviewFocus(focusedClientId);
  }, [focusedClientId, applyPreviewFocus]);

  /**
   * Create a canvas and container for a client
   */
  const createClientDOM = useCallback((clientId: string): { canvas: HTMLCanvasElement; container: HTMLElement } => {
    const canvas = document.createElement('canvas');
    canvas.width = 300;
    canvas.height = 300;
    canvas.className = 'sandbox-canvas';
    canvas.dataset.clientId = clientId;
    canvas.style.cssText = `
      flex: 1;
      display: block;
      outline: none;
      border: none;
      box-shadow: none;
      background-color: #000;
      width: 100%;
      height: 100%;
      max-width: 100%;
      max-height: 100%;
      min-width: 1px;
      min-height: 1px;
      image-rendering: pixelated;
    `;

    const container = document.createElement('div');
    container.className = 'sandbox-canvas-wrapper';
    container.dataset.clientId = clientId;
    container.appendChild(canvas);

    return { canvas, container };
  }, []);

  /** Boots the live preview — the body of `run`, which a test run's resume also calls from inside its transition. */
  const runNow = useCallback(async (systems: SandboxSystemsInput | ProjectTree) => {
    lastRunSystemsRef.current = systems;
    // Stop any existing runtime BEFORE re-initializing: disposing its mount stops
    // every client and area loop and resets the page-wide state, so the new boot
    // starts clean and never races the old one's teardown.
    if (runtimeStateRef.current) {
      const prev = runtimeStateRef.current;
      runtimeStateRef.current = null;
      clientsWithDOMRef.current = [];
      setClients([]);
      await stopRuntime(prev);
    }

    setIsLoading(true);
    setStatus('compiling');
    setStatusText('Compiling...');

    try {
      setStatus('initializing');
      setStatusText('Initializing environment...');

      // Track newly created clients during initialization
      const newClients: ClientRecordWithDOM[] = [];

      // A live boot must never hang forever. `initializeRuntime` awaits the
      // client JOIN (environment.createClients), which can stall indefinitely
      // when a generated system's onInit/join never completes or a re-run races
      // the prior runtime's async teardown — leaving the preview stuck on
      // "Initializing environment..." with a black canvas while everything else
      // (tests, status bar) looks fine. Bound it: on timeout we surface a real
      // error instead of an eternal spinner, so the failure is visible (and the
      // eval's wait-for-"Running" fails honestly rather than passing on a stale
      // status). A normal boot is well under a second; 20s is pure slack. A boot
      // that settles after the timeout is disposed when it does, so it never
      // stays mounted beside the next; giving up aborts the playground's mount,
      // which frees the page at once.
      const giveUp = new AbortController();
      const booting = initializeRuntime(systems, {
        onClientCreated: (client) => {
          const { canvas, container } = createClientDOM(client.id);
          newClients.push({
            ...client,
            settings: { ...DEFAULT_CLIENT_SETTINGS },
            canvas,
            container,
          });
          return { canvas, container };
        },
      }, giveUp.signal);
      const runtimeState = await bootWithin(booting, {
        budgetMs: LIVE_BOOT_TIMEOUT_MS,
        dispose: (late) => stopRuntime(late),
        onGiveUp: () => giveUp.abort(),
        timeoutMessage:
          'Live boot timed out after 20s — the game never finished initializing ' +
          '(a system onInit/join likely never completed, or a re-run raced the ' +
          'previous runtime\'s teardown).',
      });

      // The runtime's records are in surface order: each surface's record becomes the client drawing on it.
      newClients.forEach((newClient, index) => adoptRuntimeClient(newClient, runtimeState.clients[index]));

      runtimeStateRef.current = runtimeState;
      clientsWithDOMRef.current = newClients;
      setClients([...newClients]);
      setFocusedClientId(newClients[0]?.id ?? null);
      // Applied here as well as by the effect: a re-boot keeps the same highlighted id, so the effect would not run.
      applyPreviewFocus(newClients[0]?.id ?? null);

      setStatus('running');
      setStatusText(`Running (${newClients.length} player${newClients.length > 1 ? 's' : ''})`);
      console.info('[Sandbox] Game is running!');
    } catch (err) {
      console.error('[Sandbox] Failed to run:', err);
      // Best-effort cleanup of any partial runtime so a timed-out/failed boot
      // doesn't leak a half-initialized client into the next run.
      if (runtimeStateRef.current) {
        const partial = runtimeStateRef.current;
        runtimeStateRef.current = null;
        await stopRuntime(partial);
      }
      clientsWithDOMRef.current = [];
      setClients([]);
      setStatus('error');
      setStatusText(err instanceof BootTimeoutError ? 'Boot timed out' : 'Error');
    } finally {
      setIsLoading(false);
    }
  }, [createClientDOM, applyPreviewFocus]);

  const run = useCallback(
    (systems: SandboxSystemsInput | ProjectTree) => enqueuePreview(() => runNow(systems)),
    [enqueuePreview, runNow],
  );

  const stop = useCallback(() => {
    // The UI stops at once. The mount is disposed in turn, after any transition already under way — so a boot still
    // in flight when Stop is pressed is stopped too, once it lands.
    const stopUi = () => {
      clientsWithDOMRef.current = [];
      setClients([]);
      setFocusedClientId(null);
      setStatus('stopped');
      setStatusText('Stopped');
    };
    stopUi();
    console.info('[Sandbox] Game stopped');
    void enqueuePreview(async () => {
      const state = runtimeStateRef.current;
      if (!state) return;
      runtimeStateRef.current = null;
      stopUi();
      await stopRuntime(state);
    });
  }, [enqueuePreview]);

  /** Adds a client to the running preview — the body of `addClient`, which a test run's resume also calls. */
  const addClientNow = useCallback(async () => {
    if (!runtimeStateRef.current) {
      console.warn('[Sandbox] Cannot add client - sandbox not running');
      return;
    }

    try {
      // Use a mutable object to capture the client with DOM from the callback
      const capturedClient: { value: ClientRecordWithDOM | null } = { value: null };

      const { client, nextCounter } = await addClientToRuntime(
        runtimeStateRef.current,
        {
          onClientCreated: (c) => {
            const { canvas, container } = createClientDOM(c.id);
            capturedClient.value = {
              ...c,
              settings: { ...DEFAULT_CLIENT_SETTINGS },
              canvas,
              container,
            };
            return { canvas, container };
          },
        }
      );

      const newClientWithDOM = capturedClient.value;
      if (newClientWithDOM) {
        adoptRuntimeClient(newClientWithDOM, client);
        runtimeStateRef.current.clients.push(client);
        runtimeStateRef.current.nextClientIdCounter = nextCounter;
        clientsWithDOMRef.current.push(newClientWithDOM);

        setClients(prev => [...prev, newClientWithDOM]);
        setStatusText(`Running (${clientsWithDOMRef.current.length} players)`);
        console.info(`[Sandbox] Added client ${client.id}`);
      }
    } catch (err) {
      console.error('[Sandbox] Failed to add client:', err);
    }
  }, [createClientDOM]);

  const addClient = useCallback(() => enqueuePreview(addClientNow), [enqueuePreview, addClientNow]);

  /** Tops the running preview up to `count` clients — a restored document's; the project's definition may already boot some or all of them. */
  const restoreClients = useCallback((count: number) => enqueuePreview(async () => {
    const missing = count - clientsWithDOMRef.current.length;
    for (let added = 0; added < missing; added++) await addClientNow();
  }), [enqueuePreview, addClientNow]);

  const removeClient = useCallback((id: string) => enqueuePreview(async () => {
    if (!runtimeStateRef.current) return;

    await removeClientFromRuntime(runtimeStateRef.current, id);

    runtimeStateRef.current.clients = runtimeStateRef.current.clients.filter(c => c.id !== id);
    clientsWithDOMRef.current = clientsWithDOMRef.current.filter(c => c.id !== id);
    setClients(prev => prev.filter(c => c.id !== id));
    
    // If we removed the focused client, focus the first remaining one
    if (focusedClientId === id) {
      const remaining = clientsWithDOMRef.current;
      setFocusedClientId(remaining[0]?.id ?? null);
    }

    const count = clientsWithDOMRef.current.length;
    setStatusText(`Running (${count} player${count !== 1 ? 's' : ''})`);
    console.info(`[Sandbox] Client ${id} removed. Active Players: ${count}`);
  }), [focusedClientId, enqueuePreview]);

  const focusClient = useCallback((id: string) => {
    // Route keyboard input to ONLY this client (focus-scoped-input, by the
    // client's input token). The focus is internal — system code never reads it;
    // the only visible effect is the client's highlight. (Focus is a SET under the
    // hood, so the test driver can focus several at once; the live UI focuses one
    // per click.)
    applyPreviewFocus(id);
    setFocusedClientId(prev => {
      if (prev === id) return prev;
      console.info(`[Sandbox] Focused client: ${id}`);
      return id;
    });
  }, [applyPreviewFocus]);

  const resize = useCallback(() => {
    // In the React version, resize is handled by CSS Grid layout
    // No manual DOM manipulation needed
  }, []);

  /**
   * Update settings for a specific client
   */
  const updateClientSettings = useCallback((id: string, settings: Partial<GboxClientSettings>) => {
    if (runtimeStateRef.current) {
      const runtimeClient = runtimeStateRef.current.clients.find(c => c.id === id);
      if (runtimeClient) {
        runtimeClient.settings = { ...runtimeClient.settings, ...settings };
        if (typeof settings.simulatedPing === 'number' && runtimeClient.instance) {
          runtimeClient.instance.simulatedLatency = settings.simulatedPing;
        }
        // Peer ping: null/undefined => inherit server ping (leave simulatedPeerLatency unset)
        if ('simulatedPeerPing' in settings && runtimeClient.instance) {
          runtimeClient.instance.simulatedPeerLatency =
            settings.simulatedPeerPing == null ? undefined : (settings.simulatedPeerPing as number);
        }
      }
      if (typeof settings.simulatedPing === 'number') {
        runtimeStateRef.current.latencyByClientId.set(id, settings.simulatedPing);
      }
    }
    setClients(prev => prev.map(client => {
      if (client.id === id) {
        return {
          ...client,
          settings: { ...client.settings, ...settings },
        };
      }
      return client;
    }));
    
    // Also update the ref
    const clientWithDOM = clientsWithDOMRef.current.find(c => c.id === id);
    if (clientWithDOM) {
      clientWithDOM.settings = { ...clientWithDOM.settings, ...settings };
      if (typeof settings.simulatedPing === 'number' && clientWithDOM.instance) {
        clientWithDOM.instance.simulatedLatency = settings.simulatedPing;
      }
      if ('simulatedPeerPing' in settings && clientWithDOM.instance) {
        clientWithDOM.instance.simulatedPeerLatency =
          settings.simulatedPeerPing == null ? undefined : (settings.simulatedPeerPing as number);
      }
    }
    
    console.info(`[Sandbox] Updated client ${id} settings:`, settings);
  }, []);

  /**
   * Get all client settings for export/persistence
   */
  const getClientSettings = useCallback(() => {
    return clientsWithDOMRef.current.map(client => ({
      id: client.id,
      displayName: client.displayName,
      settings: client.settings,
    }));
  }, []);

  /** Module map for in-browser test execution */
  const testExternalModules = useMemo(() => new Map<string, any>([
    ['@gotti/client', GottiClient],
    ['@gotti/server', GottiServer],
    ['@gotti/core', GottiCore],
    ['@gotti/systems', GottiCore],
    // Browser sandbox sees every export from @gotti/test-utils PLUS the
    // browser-safe versions from /harness-browser. The merge order is
    // critical: TestUtils (re-exports from harness.ts, the NODE version)
    // first, BrowserTestUtils LAST — so the browser `createTestHarness`,
    // `setupSystemPair`, `TestProcess`, etc. WIN on collision. Without
    // this, the test runtime calls the Node harness which uses the real
    // `ServerProcess` (and lacks the lazy auto-start / connector wiring
    // / forwardToServer extensions added for the in-browser path), and
    // every client→server dispatch silently goes nowhere. The original
    // ordering was inverted; fixing it was the root cause of the snake-v2
    // input-dispatch tests stalling at "Expected length 1 but received 0"
    // for the last ~10 eval iterations.
    ['@gotti/test-utils', { ...TestUtils, ...BrowserTestUtils }],
    ['@gotti/sandbox-test', {
      describe: testDescribe,
      it: testIt,
      test: testTest,
      expect: testExpect,
      beforeEach: testBeforeEach,
      afterEach: testAfterEach,
      beforeAll: testBeforeAll,
      afterAll: testAfterAll,
      defineSandboxTest: coreDefineSandboxTest,
    }],
  ]), []);

  /** What a test run suspended: the code the preview last ran and its clients' settings, in order. */
  type SuspendedPreview = { systems: SandboxSystemsInput | ProjectTree; clients: { id: string; settings: GboxClientSettings }[] };

  /**
   * Suspends the live preview for a test run: its mount is disposed — the page holds one game environment — but its
   * on-screen canvases stay, because the test booter reuses them when there are enough. Null when no preview is
   * running. Called inside the test run's preview transition.
   */
  const suspendPreview = useCallback(async (): Promise<SuspendedPreview | null> => {
    const state = runtimeStateRef.current;
    const systems = lastRunSystemsRef.current;
    if (!state || !systems) return null;
    const suspended: SuspendedPreview = {
      systems,
      clients: clientsWithDOMRef.current.map((c) => ({ id: c.id, settings: { ...c.settings } })),
    };
    runtimeStateRef.current = null;
    await stopRuntime(state);
    return suspended;
  }, []);

  /**
   * Resumes a suspended preview after a test run: the same code, client count and per-client settings, freshly
   * booted — the restore the URL-load path performs (main.tsx). Called inside the test run's preview transition.
   */
  const resumePreview = useCallback(async (suspended: SuspendedPreview | null): Promise<void> => {
    if (!suspended) return;
    await runNow(suspended.systems);
    // A boot that failed is already reported (status + console) by runNow.
    if (!runtimeStateRef.current) return;
    while (clientsWithDOMRef.current.length < suspended.clients.length) {
      const before = clientsWithDOMRef.current.length;
      await addClientNow();
      // A client that failed to join is already reported by addClientNow; stop rather than retry forever.
      if (clientsWithDOMRef.current.length === before) break;
    }
    suspended.clients.forEach((client, index) => {
      const resumed = clientsWithDOMRef.current[index];
      if (resumed) updateClientSettings(resumed.id, client.settings);
    });
  }, [runNow, addClientNow, updateClientSettings]);

  /**
   * Compiles and runs the spec files `testPaths` from a flat virtual filesystem — the body of a test run, which
   * `executeTests` runs with the live preview suspended.
   */
  const runTestFiles = useCallback(async (
    virtualFiles: Map<string, string>,
    testPaths: string[],
    /** Generated assets a test:request pins for this run — overlaid onto the
     *  session asset store by createTestAssetSource (refs WIN by path).
     *  Absent ⇒ session store only, exactly the pre-assets behavior. */
    assets?: TestAssetRef[],
    /**
     * A panel run's extras: `only` one test and `signal` to cancel (the engine's own options); `broadcast: false` and
     * `complete: false` when the run is the spec half of a Run All, which broadcasts and completes the whole run itself.
     */
    options: { only?: { suiteName: string; testName: string }; signal?: AbortSignal; broadcast?: boolean; complete?: boolean } = {},
  ): Promise<TestRunResult> => {
    // ── Multiplayer-coverage observation (eval/e2e signal) ───────────────
    // Bridge two facts the runtime keeps in separate places: the booter knows
    // how many clients a test spun up (clientCount) but NOT which test; the
    // progress stream knows the running test but NOT its client count. Recording
    // both here lets us report, per test, the max clients it booted — so an eval
    // can prove a `clients>=2` test actually RAN and PASSED (not merely that 2
    // canvases flickered on screen). Kept OUT of the byte-compared TestRunResult
    // (the fix-loop coordinator diffs that JSON); surfaced only on the separate
    // postMessage envelope below. Generic — any multi-client test, any game.
    const clientsByTest = new Map<string, number>();
    let bootingTestKey: string | null = null;
    let bootingTest: { suiteName: string; testName: string } | null = null;
    const testKey = dslTestKey;
    const trackProgress = (ev: TestProgressEvent): void => {
      if (ev.type === 'test-start') {
        bootingTestKey = testKey(ev.file, ev.suiteName, ev.testName);
        bootingTest = { suiteName: ev.suiteName, testName: ev.testName };
      } else if (ev.type === 'test-end') {
        bootingTestKey = null;
        bootingTest = null;
      }
      onTestProgress(ev);
    };
    // Each `defineSandboxTest` test's last progress on the playground's runner, by its key: its steps — on its row as it
    // runs, then on its shown result once the run is over.
    const dslRuns = new Map<string, PlaygroundTestProgress>();
    const showDslSteps = (test: { suiteName: string; testName: string }, progress: PlaygroundTestProgress): void => {
      const item = progressRef.current.suites
        .find((suite) => suite.name === test.suiteName)
        ?.tests.find((candidate) => candidate.name === test.testName && candidate.state === 'running');
      if (!item) return;
      item.steps = dslStepViews(progress);
      setTestProgress({ ...progressRef.current, suites: [...progressRef.current.suites] });
    };

    // Asset seam for the per-test boots: install a boot override so each
    // `bootRealRuntime` builds its per-boot ServerAssetManager over
    // `createTestAssetSource(assets)` (session store + any test:request refs,
    // refs winning by path) and preloads the checked assets before systems
    // start — a sprite-drawing game's `$assets.client.getImage(...)` is thus
    // sync-ready on the first tick. NO shared global singleton anymore, so a
    // concurrently-running LIVE game keeps its OWN per-boot manager untouched
    // (the old __reset()/__init() cross-game collision is gone). The CanvasProbe
    // asset host is created PER BOOT from that manager (in the booter below),
    // since the manager only exists once a boot has happened.
    setBootAssetSeamOverride({ source: createTestAssetSource(assets), preload: checkedAssetPreloadPaths() });
    // A mutable box, not a bare `let`: the only assignment happens inside the
    // nested booter callback below, which TypeScript's control-flow analysis
    // cannot see from out here — it kept the bare `let` narrowed to its `null`
    // initializer, so `testAssetHost?.clearAccessLog()` was a call on `never`.
    const testAssetHost: { current: TestAssetHost | null } = { current: null };
    try {
      // Create coverage tracker for this test run
      const tracker = new CoverageTracker();
      const moduleCache = new Map<string, any>();
      const opts = { coverageTracker: tracker };

      // Load non-test files first (with instrumentation)
      for (const [path] of virtualFiles) {
        if (!/\.(spec|test)\.(ts|tsx)$/.test(path)) {
          try { compileAndLoad(path, virtualFiles, moduleCache, testExternalModules, console, opts); } catch {}
        }
      }

      // Dedicated OFF-SCREEN canvas PER booted client. The booted game's client
      // RenderSystem paints onto its own client's canvas and the CanvasProbe
      // reads that same one, so a rendering assertion (`canvas.expectNotBlank()`)
      // is SELF-CONTAINED: it verifies the BOOTED game actually drew, independent
      // of any live on-screen game. Pointing the booter at the live
      // `.sandbox-canvas` instead would (a) make the assertion silently depend on
      // a running live game and (b) fight the live game for the same canvas. A
      // detached canvas fully supports 2D drawing + getImageData, so it needs no
      // DOM attachment and stays invisible. 300×300 matches the live client
      // canvas (`createClientDOM`) so canvas-relative physics/bounds behave
      // identically. The booter receives the declared client count
      // (`test.clients ?? 1`) and builds exactly that many.
      // Tests ALWAYS run on the REAL ClientProcess/ServerProcess/transport under
      // the virtual clock — the in-app eval + live preview are the real runtime
      // at all times, so we always know whether it works. (The old harness-mirror
      // "emergency fallback" behind an always-true REAL_RUNTIME constant was dead
      // code, and no longer worked: its input had no client tokens. Removed.)

      const makeHarnessCanvas = () => {
        const canvas = document.createElement('canvas');
        canvas.width = 300;
        canvas.height = 300;
        const ctx = canvas.getContext('2d');
        // Record drawImage calls against this client's canvas so the
        // CanvasProbe's asset assertions (expectSpriteDrawn/drawnSprites) can
        // see which sprites the booted game actually drew. Direct instance
        // patch, forwards exactly — everything else about the ctx is untouched.
        if (ctx) instrumentContext(canvas, ctx);
        const container = document.createElement('div');
        container.appendChild(canvas);
        return { canvas, ctx, container };
      };

      setSandboxGameBooter(async (clientCount = 1, bootOpts?: PlaygroundBootOptions) => {
        const n = Math.max(1, clientCount);
        // Per-test transport latency (ms). Default 0 = instant/deterministic
        // (behavioral tests); a netcode/lag-comp test sets `latency` in its def.
        const latencyMs = Math.max(0, bootOpts?.latencyMs ?? 0);
        // Attribute this boot's client count to the test currently running
        // (set by trackProgress on the surrounding test-start event).
        if (bootingTestKey) {
          clientsByTest.set(bootingTestKey, Math.max(clientsByTest.get(bootingTestKey) ?? 0, n));
        }
        // Render the test's clients onto the canvases ALREADY in the live
        // preview, so the surface the user is watching IS the one the test
        // drives — the test client maps 1:1 onto the on-screen client, single-
        // AND multi-client alike (not just `clients: N`). Reuse the existing
        // on-screen client windows whenever the UI already has enough of them;
        // only fall back to minted off-screen surfaces + the split-screen
        // overlay when the preview has FEWER windows than the test needs (e.g.
        // a `clients: N` test run before opening N windows, or before Run).
        const onScreen = clientsWithDOMRef.current;
        let surfaces: { canvas: HTMLCanvasElement; ctx: CanvasRenderingContext2D | null; container: HTMLElement }[];
        let viz: ReturnType<typeof mountTestVisualization> = null;
        if (onScreen.length >= n) {
          surfaces = onScreen.slice(0, n).map((c) => {
            const ctx = c.canvas.getContext('2d');
            // idempotent — re-instrumenting a reused canvas is a no-op
            if (ctx) instrumentContext(c.canvas, ctx);
            return { canvas: c.canvas, ctx, container: c.container };
          });
        } else {
          surfaces = Array.from({ length: n }, makeHarnessCanvas);
          // Show the (minted) clients live in the preview while the test runs.
          viz = mountTestVisualization(surfaces);
        }
        // Boot the actual ClientProcess/ServerProcess/transport under the virtual
        // clock. It paints onto the SAME surfaces — visible in the preview + read
        // by CanvasProbe. The virtual clock is installed before the run loop below.
        // Share the SAME module universe the spec was compiled with: the spec's
        // `@/shared/*` classes (components/entities) MUST be identical to the
        // runtime systems' — else a component the spec attaches is invisible to
        // `system.getComponent(SameClass)` and authoritative movement no-ops.
        // `new Map(moduleCache)` carries the already-compiled class instances
        // (cache values are shared refs) while isolating per-boot additions.
        // The test runs on the mount's runner (the game's `runTest`), as in the node runner.
        const realGame = await bootRealRuntime(n, surfaces, virtualFiles, {
          externalModules: testExternalModules,
          moduleCache: new Map(moduleCache),
        }, latencyMs, bootOpts?.test);
        // Point the CanvasProbe asset host at THIS boot's manager (the one the
        // booted game's `$assets` reads), built + preloaded inside
        // bootRealRuntime. Recreated per boot: each test boots a fresh manager,
        // so a stale host would resolve against the wrong (or empty) cache.
        const bootManager = getLastBootAssetManager();
        if (bootManager) {
          testAssetHost.current = createTestAssetHost(bootManager);
          setAssetHost(testAssetHost.current);
        }
        // The test runs on its mount's runner: each progress snapshot is its steps in the Tests panel.
        const key = bootingTestKey;
        const forTest = bootingTest;
        if (key && forTest && runsOnPlayground(realGame)) {
          const runTest = realGame.runTest;
          realGame.runTest = (onProgress) => runTest((progress) => {
            dslRuns.set(key, progress);
            showDslSteps(forTest, progress);
            onProgress?.(progress);
          });
        }
        if (viz) {
          const baseDisconnect = realGame.disconnect;
          realGame.disconnect = (clientIndex: number) => {
            viz.removeClient(clientIndex);
            return baseDisconnect?.(clientIndex);
          };
        }
        return realGame;
      });

      // Run test files (tests themselves are NOT instrumented)
      const allSuites: TestRunResult['suites'] = [];
      let totalTests = 0, totalPassed = 0, totalFailed = 0;
      let cancelled = false;
      const loadErrorSuites: TestRunResult['suites'] = [];
      const start = performance.now();

      // Seed Math.random + freeze the clock for the whole test run — the same
      // determinism shim the Node/Browser runner adapters install. Without this
      // the eval ran generated games with REAL unseeded Math.random, so tests
      // that read RNG-driven state (snake spawn/respawn cells → whether the snake
      // collides + respawns, resetting direction) flipped pass/fail run-to-run.
      // Installed AFTER `start` (real perf.now) and restored in `finally` BEFORE
      // the duration is read, so run telemetry stays real wall-clock.
      // The VIRTUAL CLOCK virtualizes timers so the real async runtime + simulated-
      // latency transport run deterministically under manual ticks, and seeds
      // Math.random with mulberry32(0).
      // Installed and given back by `runOnClock` — the one helper BrowserRunnerAdapter runs on too — by its own
      // install: a run that outlived its test leaves a later run's clock installed.
      await runOnClock('virtual', { random: 0, clockStart: 0 }, async () => {
      for (const testPath of testPaths) {
        if (options.signal?.aborted) { cancelled = true; break; }
        resetTests();
        // Fresh access log per spec file — `wasAccessed` diagnostics (the
        // "loaded but never read" hints in expectSpriteDrawn failures) must
        // reflect THIS spec's reads, not reads accumulated by earlier specs.
        // (Null until the spec's first boot creates a manager; each boot also
        // installs a fresh host with an empty log, so this is belt-and-braces.)
        testAssetHost.current?.clearAccessLog();
        // Tag every suite/test this spec registers with its sandbox-relative path
        // (matching the discovery preview's `file` convention: no leading slash,
        // keeps `.ts`). This makes the RESULT self-describing so the Tests panel
        // can resolve a clicked row → spec file directly from the result, instead
        // of depending on the debounced discovery map having populated first.
        const specFile = testPath.replace(/^\//, '');
        setCurrentTestFile(specFile);
        try {
          const loaded = loadSpecFile(compileAndLoad, testPath, virtualFiles, moduleCache, testExternalModules, console, opts);
          if ('loadError' in loaded) loadErrorSuites.push(specLoadErrorSuite(specFile, loaded.loadError));
          const result = await runCollectedTests({ onProgress: trackProgress, only: options.only, signal: options.signal });
          allSuites.push(...result.suites);
          totalTests += result.totalTests;
          totalPassed += result.totalPassed;
          totalFailed += result.totalFailed;
          if (result.cancelled) cancelled = true;
        } catch (err) {
          // Anything else a spec's load or run throws is its load error too, so the result shape stays a failed run.
          loadErrorSuites.push(specLoadErrorSuite(specFile, err));
        } finally {
          setCurrentTestFile(undefined);
        }
      }
      // A spec that does not load is reported after every spec's tests, as the node and browser runners report it.
      allSuites.push(...loadErrorSuites);
      totalTests += loadErrorSuites.length;
      totalFailed += loadErrorSuites.length;
      });

      // Tear down the multi-client test visualization overlay (if any was shown).
      clearTestVisualization();

      // Collect coverage from instrumented files. `coverageReport` state keeps
      // the FULL `CoverageReport` (per-line hit arrays) for this component's
      // own local coverage UI; `TestRunResult.coverage` carries the bounded
      // wire shape (percentage + per-file `uncoveredLines`) that flows out of
      // runTests()/runTestsFromFiles() into `AgentContext.tsx`'s
      // `postTestResults` call and on to the deep agent's coverage gate
      // (`get_coverage` reads these exact line numbers) — never the raw
      // per-line hit counters, which never need to leave this process.
      const report: CoverageReport = tracker.collect(virtualFiles);
      setCoverageReport(report);

      const coverageFiles = report.files as unknown as Array<{
        path: string;
        percentage: number;
        lines: Array<{ line: number; hits: number }>;
      }>;
      const finalResult: TestRunResult = {
        suites: allSuites,
        totalTests, totalPassed, totalFailed, totalSkipped: 0,
        ...(cancelled ? { cancelled: true as const } : {}),
        duration: performance.now() - start,
        coverage: {
          percentage: report.percentage,
          files: coverageFiles.map((f) => ({
            path: f.path,
            percentage: f.percentage,
            uncoveredLines: f.lines
              .filter((l) => l.hits === 0)
              .map((l) => l.line)
              .sort((a, b) => a - b)
              .slice(0, 200),
          })),
        },
      };

      // Mark streaming progress as complete (after all files processed) — unless a Run All goes on to its playgrounds.
      if (options.complete !== false) {
        progressRef.current.phase = 'complete';
        setTestProgress({ ...progressRef.current, suites: [...progressRef.current.suites] });
      }

      // Per-test multi-client coverage: correlate the client count each test
      // booted (clientsByTest) with its pass/fail. clients>=2 ⇒ a real
      // multiplayer test that boots multiple split-screen clients.
      const multiClientTests: Array<{
        suiteName: string;
        testName: string;
        file?: string;
        clients: number;
        status: string;
      }> = [];
      for (const s of allSuites) {
        for (const t of s.tests) {
          const file = t.file ?? s.file;
          const clients = clientsByTest.get(testKey(file, s.name, t.name)) ?? 1;
          if (clients >= 2) {
            multiClientTests.push({ suiteName: s.name, testName: t.name, file, clients, status: t.status });
          }
        }
      }

      // The steps of the DSL tests this run ran, for the panels' view of its results. A test the run ran is forgotten first,
      // so one that is no longer a `defineSandboxTest` test shows no steps it used to have.
      const nextDslSteps = new Map(dslStepsRef.current);
      for (const suite of allSuites) for (const test of suite.tests) nextDslSteps.delete(testKey(test.file ?? suite.file, suite.name, test.name));
      for (const [key, progress] of dslRuns) nextDslSteps.set(key, { id: progress.testId, steps: dslStepViews(progress) });
      dslStepsRef.current = nextDslSteps;
      setDslSteps(nextDslSteps);

      // Broadcast final results via postMessage (for parent/iframe consumers) — a Run All broadcasts its whole result
      // itself, with this run's multi-client sidecar, which it reads back from the ref.
      lastMultiClientRef.current = multiClientTests;
      if (options.broadcast !== false) broadcastTestResults(finalResult, multiClientTests);

      return finalResult;
    } finally {
      // Tear the asset bridge back down: drop the probe host and the boot
      // override. No singleton `__reset()`/re-`__init()` dance anymore — each
      // test boot owned its OWN per-boot ServerAssetManager (disposed with its
      // env), and a concurrently-running LIVE game keeps its own manager, so
      // there is nothing shared to restore. The next live/test boot builds its
      // manager fresh from the (default/overridden) asset seam.
      setAssetHost(null);
      setBootAssetSeamOverride(null);
    }
  }, [testExternalModules, onTestProgress]);

  /**
   * Core test execution: compile & run test files from a flat virtual filesystem.
   *
   * A test run takes over the page's one game environment: it suspends the live preview, runs, and resumes the
   * preview — the same code, client count and settings, freshly booted — whatever the run's outcome. The whole run
   * is one preview transition, so a Run, Stop or added client asked for meanwhile waits its turn.
   */
  const executeTests = useCallback(async (
    virtualFiles: Map<string, string>,
    testFilePaths?: string[],
    /** Generated assets a test:request pins for this run (see `runTestFiles`). */
    assets?: TestAssetRef[],
  ): Promise<TestRunResult> => {
    // Optional subset filter: the fix loop's closed inner loop runs ONLY a
    // cluster's failing spec files. Normalize both sides to a leading slash so
    // the allowlist matches regardless of how the caller passed the paths.
    const allow = testFilePaths && testFilePaths.length > 0
      ? new Set(testFilePaths.map(p => (p.startsWith('/') ? p : `/${p}`)))
      : null;
    const testPaths = [...virtualFiles.keys()]
      .filter(p => /\.(spec|test)\.(ts|tsx)$/.test(p))
      .filter(p => !allow || allow.has(p.startsWith('/') ? p : `/${p}`));
    if (testPaths.length === 0) {
      setCoverageReport(null);
      return { suites: [], totalTests: 0, totalPassed: 0, totalFailed: 0, totalSkipped: 0, duration: 0 };
    }

    // The result goes back as soon as the tests finish; the preview's resume is the transition's tail, so an AI test
    // request is not held up by the preview booting again (previewTransitions.ts).
    return runWithPreviewSuspended(previewQueueRef.current, suspendPreview, resumePreview, () => runTestFiles(virtualFiles, testPaths, assets));
  }, [suspendPreview, runTestFiles, resumePreview]);

  /**
   * Run test files (*.spec.ts / *.test.ts) in-browser and return results.
   */
  const runTests = useCallback(async (systems: SandboxSystemsInput): Promise<TestRunResult> => {
    setIsRunningTests(true);
    try {
      // Build virtual filesystem from structured input
      const virtualFiles = new Map<string, string>();
      const addFolder = (prefix: string, folder: typeof systems.shared) => {
        folder.rootFiles.forEach(f => {
          virtualFiles.set(`${prefix}/${f.name}.ts`, f.code);
        });
        for (const [sub, files] of Object.entries(folder.subfolders)) {
          files.forEach(f => {
            virtualFiles.set(`${prefix}/${sub}/${f.name}.ts`, f.code);
          });
        }
      };
      addFolder('/shared', systems.shared);
      addFolder('/client', systems.client);
      addFolder('/server', systems.server);

      const result = await executeTests(virtualFiles);
      // Strip the synthetic boot-smoke suite from the DISPLAYED copy only —
      // mirrors the postMessage broadcast below so the TESTS/editor panels
      // agree with SSE (the returned `result` stays raw for callers that
      // chain off it, e.g. postTestResults → backend/checkpoint parity).
      setTestResults(stripSyntheticSuites(result));
      return result;
    } finally {
      setIsRunningTests(false);
    }
  }, [executeTests]);

  /**
   * Run tests from a flat file map (used by agent-delegated test execution).
   */
  const runTestsFromFiles = useCallback(async (files: Record<string, string>, testFilePaths?: string[], assets?: TestAssetRef[]): Promise<TestRunResult> => {
    // Mirror the EXACT file set we're about to run so the Tests panel's pre-run
    // discovery reflects what the worker is testing (these arrive as flat paths
    // the editor `systems` can't hold).
    syncAgentFiles(files);
    setIsRunningTests(true);
    try {
      const virtualFiles = new Map(Object.entries(files));
      const result = await executeTests(virtualFiles, testFilePaths, assets);
      // Same display-only strip as runTests above — the returned `result`
      // stays raw for the agent-delegated caller chain.
      setTestResults(stripSyntheticSuites(result));
      return result;
    } finally {
      setIsRunningTests(false);
    }
  }, [executeTests, syncAgentFiles]);

  /**
   * Runs the project's playground tests — every playground that declares tests, or `only` one test of one — inside the
   * caller's preview transition. Each playground is mounted through the preview's own boot (`bootRuntimeFromProject`)
   * onto the on-screen canvases (detached ones beyond them), and each test runs alone (`targetLabel`) under the site's
   * bound — its `timeoutMs`, the ready budget when it has a ready or game gate, and 5 s — since the playground bounds
   * only a timeline's wait for its next step. Its progress streams into `testProgress`, and its console lines are kept
   * per step. A playground that does not load — or does not list or boot within the site's boot budget — is a suite with
   * one load error; a rerun (`only`) reports only its own file's. Once `signal` aborts, the test running then ends as
   * cancelled and no other starts; a playground still listing or booting is given up and is no suite of the result.
   */
  const runPlaygroundTests = useCallback(async (
    project: ProjectTree,
    options: { signal: AbortSignal; only?: { file: string; playground: string; testId: string } },
  ): Promise<SuiteResult[]> => {
    let listing: Awaited<ReturnType<typeof listProjectPlaygrounds>>;
    try {
      listing = await bootWithin(listProjectPlaygrounds(project.files, sandboxExternals()), {
        budgetMs: LIVE_BOOT_TIMEOUT_MS,
        signal: options.signal,
        dispose: () => undefined,
        timeoutMessage: `The project's playgrounds did not load within ${LIVE_BOOT_TIMEOUT_MS / 1000} s — a config or definition function never settled.`,
      });
    } catch (err) {
      if (err instanceof BootCancelledError) return [];
      return [playgroundLoadErrorSuite(PLAYGROUND_CONFIG_PATH, err instanceof Error ? err.message : String(err))];
    }
    const suites: SuiteResult[] = [];
    for (const error of listing.errors) {
      if (!options.only || error.file === options.only.file) suites.push(playgroundLoadErrorSuite(error.file, error.message));
    }
    const flush = () => setTestProgress({ ...progressRef.current, suites: [...progressRef.current.suites] });

    for (const playground of listing.playgrounds) {
      if (options.signal.aborted) break;
      if (options.only && playground.name !== options.only.playground) continue;
      const listed = options.only ? playground.tests.filter((test) => test.id === options.only!.testId) : playground.tests;
      if (!listed.length) continue;
      if (progressRef.current.phase !== 'running') progressRef.current = { suites: [], phase: 'running' };
      const progressSuite: SuiteProgressItem = { name: playground.name, state: 'running', tests: listed.map((test) => ({ name: test.label, state: 'pending' })), collapsed: false };
      progressRef.current.suites = [...progressRef.current.suites, progressSuite];
      flush();

      // A test's clients draw where the preview's did; a test that needs more clients than the preview had gets detached canvases.
      const onScreen = clientsWithDOMRef.current;
      const surfaceFor = (index: number) => {
        const existing = onScreen[index];
        if (existing) return { canvas: existing.canvas, container: existing.container };
        const canvas = document.createElement('canvas');
        canvas.width = 300;
        canvas.height = 300;
        const container = document.createElement('div');
        container.appendChild(canvas);
        return { canvas, container };
      };
      let state: RuntimeState;
      try {
        // `null` latency: a test runs on the latency its project declares (`mockConfig`), as under `gotti playground`,
        // not the preview's per-client ping. Giving the boot up aborts the mount, which frees the page at once.
        const giveUp = new AbortController();
        const booting = bootRuntimeFromProject(project, { onClientCreated: (_client, _isFirst, index) => surfaceFor(index) }, null, {
          file: playground.file,
          ...(playground.member === undefined ? {} : { member: playground.member }),
          ...(playground.variant === undefined ? {} : { variant: playground.variant }),
          name: playground.name,
        }, giveUp.signal);
        state = await bootWithin(booting, {
          budgetMs: LIVE_BOOT_TIMEOUT_MS,
          signal: options.signal,
          dispose: (late) => stopRuntime(late),
          onGiveUp: () => giveUp.abort(),
          timeoutMessage: `Playground "${playground.name}" did not boot within ${LIVE_BOOT_TIMEOUT_MS / 1000} s — its setup or a client's join never finished.`,
        });
      } catch (err) {
        if (err instanceof BootCancelledError) {
          // Cancelled while it booted: none of its tests ran, so it is no suite of the result.
          progressRef.current.suites = progressRef.current.suites.filter((suite) => suite !== progressSuite);
          flush();
          break;
        }
        suites.push(playgroundLoadErrorSuite(playground.file, err instanceof Error ? err.message : String(err)));
        progressSuite.state = 'failed';
        progressSuite.tests = [];
        flush();
        continue;
      }

      // One run of the playground's tests, as `gotti playground --test` runs them: its `ready` and `game` once for the
      // run. The playground bounds only a timeline's wait for its next step, so the site watches the run: the gates,
      // before the first test starts, against their budget and 5 s; then each test, from its first progress, against
      // its own timeoutMs and 5 s. A test that overruns ends the run there and is reported timed out; the tests after
      // it then run one at a time, each its own run (their gates run again — the price of going on).
      const selection = state.mount.selection;
      const capture = new ConsoleCapture({ verbose: true });
      const logsFor = (testId: string) => (stepIndex: number) => capture.linesFor(testId, stepIndex);
      const entries = new Map(listed.map((test, index) => [test.id, { test, item: progressSuite.tests[index] }]));
      const results = new Map<string, TestResult>();
      const record = (testId: string, outcome: PlaygroundTestRun['outcome'], progress: PlaygroundTestProgress | undefined) => {
        const { test, item } = entries.get(testId)!;
        const steps = (selection.tests.find((normalized) => normalized.id === testId)?.steps ?? []).map((step) => ({ label: step.label, atTime: step.atTime }));
        const result = playgroundTestResult({
          playground: playground.name,
          file: playground.file,
          test: { id: test.id, label: test.label, steps },
          outcome,
          progress,
          logsFor: logsFor(testId),
        });
        results.set(testId, result);
        Object.assign(item, { state: itemStateOf(result.status), error: result.error?.message, steps: result.playground?.steps });
        flush();
      };
      /** Runs `batch` — the playground's whole suite, or one test alone — records each test, and returns those an overrun left unrun. */
      const runBatch = async (batch: typeof listed, whole: boolean): Promise<typeof listed> => {
        const watchdog = new AbortController();
        let overrun: { testId: string | null; boundMs: number } | null = null;
        let timer: ReturnType<typeof setTimeout> | undefined;
        const arm = (testId: string | null, boundMs: number) => {
          if (timer) clearTimeout(timer);
          timer = setTimeout(() => {
            overrun = { testId, boundMs };
            watchdog.abort();
          }, boundMs);
        };
        arm(null, gateBudgetMs(selection) + 5_000);
        const last = new Map<string, PlaygroundTestProgress>();
        let current: string | null = null;
        let suite: PlaygroundTestSuiteResult | undefined;
        let rejected: { error: unknown } | null = null;
        try {
          suite = await withConsoleCapture(capture, () => state.mount.runTests({
            ...(whole ? {} : { targetLabel: batch[0].id }),
            signal: anySignal([options.signal, watchdog.signal]),
            onProgress: (snapshot) => {
              const entry = entries.get(snapshot.testId);
              if (!entry) return;
              last.set(snapshot.testId, snapshot);
              if (current !== snapshot.testId) {
                current = snapshot.testId;
                arm(snapshot.testId, entry.test.timeoutMs + 5_000);
              }
              Object.assign(entry.item, playgroundProgressItem(snapshot, logsFor(snapshot.testId)));
              flush();
            },
          }));
        } catch (error) {
          rejected = { error };
        } finally {
          if (timer) clearTimeout(timer);
        }
        const ended = overrun as { testId: string | null; boundMs: number } | null;
        const unrun: typeof listed = [];
        for (const test of batch) {
          const progress = last.get(test.id);
          if (rejected) record(test.id, { kind: 'rejected', error: rejected.error }, progress);
          else if (ended?.testId === null) record(test.id, { kind: 'gates-timed-out', boundMs: ended.boundMs }, progress);
          else if (ended?.testId === test.id) record(test.id, { kind: 'timed-out', boundMs: ended.boundMs }, progress);
          else if (options.signal.aborted && current === test.id && suite?.cancelled) record(test.id, { kind: 'cancelled' }, progress);
          else if (suite?.tests.some((ran) => ran.id === test.id)) record(test.id, { kind: 'result', suite }, progress);
          // Cancelled before it started: not in the result, as the engine leaves an unreached test out.
          else if (options.signal.aborted) continue;
          else if (ended) unrun.push(test);
          // The run never ran it (its gates failed, say): the suite's own error says why.
          else record(test.id, { kind: 'result', suite: suite! }, progress);
        }
        return unrun;
      };
      try {
        let pending = await runBatch(listed, !options.only);
        while (pending.length > 0 && !options.signal.aborted) {
          const [next, ...rest] = pending;
          pending = [...(await runBatch([next], false)), ...rest];
        }
      } finally {
        await stopRuntime(state);
      }
      // Tests the run never reached — it was cancelled — are not in the result, as the engine leaves them out.
      progressSuite.tests = progressSuite.tests.filter((test) => test.state !== 'pending');
      const ran = listed.flatMap((test) => results.get(test.id) ?? []);
      progressSuite.state = ran.every((test) => test.status === 'passed') ? 'passed' : 'failed';
      flush();
      suites.push(playgroundSuiteResult(playground.name, playground.file, ran));
    }
    return suites;
  }, []);

  /**
   * The panel's Run All: the project's spec files (its AI view — the AI's root `__tests__/` included) through the
   * engine, then every playground test, in one preview transition and one result — what both panels show and the one
   * `test-results` broadcast carries. Cancel aborts it.
   */
  const runAllTests = useCallback(async (project: ProjectTree): Promise<TestRunResult> => {
    const controller = new AbortController();
    panelRunRef.current = controller;
    setIsRunningTests(true);
    try {
      const files = projectSpecFiles(project);
      const specPaths = [...files.keys()].filter((path) => SPEC_FILE.test(path));
      const result = await runWithPreviewSuspended(previewQueueRef.current, suspendPreview, resumePreview, async () => {
        const start = performance.now();
        progressRef.current = { suites: [], phase: 'running' };
        setTestProgress({ ...progressRef.current });
        lastMultiClientRef.current = [];
        const specs = specPaths.length
          ? await runTestFiles(files, specPaths, undefined, { signal: controller.signal, broadcast: false, complete: false })
          : null;
        const playgroundSuites = controller.signal.aborted ? [] : await runPlaygroundTests(project, { signal: controller.signal });
        const cancelled = controller.signal.aborted || specs?.cancelled === true;
        const run = withTotals({
          suites: [...(specs?.suites ?? []), ...playgroundSuites],
          duration: performance.now() - start,
          ...(specs?.coverage ? { coverage: specs.coverage } : {}),
          ...(cancelled ? { cancelled: true as const } : {}),
        });
        progressRef.current = progressFromResult(withDslSteps(stripSyntheticSuites(run), dslStepsRef.current)!);
        setTestProgress({ ...progressRef.current });
        broadcastTestResults(run, lastMultiClientRef.current);
        return run;
      });
      setTestResults(stripSyntheticSuites(result));
      return result;
    } finally {
      if (panelRunRef.current === controller) panelRunRef.current = null;
      setIsRunningTests(false);
    }
  }, [runTestFiles, runPlaygroundTests, suspendPreview, resumePreview]);

  /** Runs one test again — a spec's by suite and name, a playground's by id — and merges it into the results on screen. */
  const rerunTest = useCallback(async (project: ProjectTree, target: RerunTarget): Promise<TestRunResult> => {
    const controller = new AbortController();
    panelRunRef.current = controller;
    setIsRunningTests(true);
    const named = target.kind === 'spec'
      ? { suiteName: target.suiteName, testName: target.testName }
      : { suiteName: target.playground, testName: target.testLabel };
    // A suite's file is the project's when the tree holds it: a spec at its AI (v1) path, a playground at its own.
    const inProject = (file: string | undefined) => {
      if (file === undefined) return false;
      const path = file.replace(/^\/+/, '');
      const treePath = treePathOfV1Path(path);
      return path in project.files || (treePath !== null && treePath in project.files);
    };
    try {
      const ran = await runWithPreviewSuspended(previewQueueRef.current, suspendPreview, resumePreview, async () => {
        const start = performance.now();
        progressRef.current = { suites: [], phase: 'running' };
        setTestProgress({ ...progressRef.current });
        lastMultiClientRef.current = [];
        let suites: SuiteResult[];
        if (target.kind === 'spec') {
          const files = projectSpecFiles(project);
          const specs = await runTestFiles(files, [`/${target.file.replace(/^\/+/, '')}`], undefined, { only: named, signal: controller.signal, broadcast: false, complete: false });
          suites = specs.suites;
        } else {
          suites = await runPlaygroundTests(project, { signal: controller.signal, only: { file: target.file, playground: target.playground, testId: target.testId } });
        }
        return withTotals({ suites, duration: performance.now() - start, ...(controller.signal.aborted ? { cancelled: true as const } : {}) });
      });
      const merged = mergeRerun(shownResultsRef.current, ran, { file: target.file, ...named }, inProject);
      progressRef.current = progressFromResult(withDslSteps(stripSyntheticSuites(merged), dslStepsRef.current)!);
      setTestProgress({ ...progressRef.current });
      setTestResults(stripSyntheticSuites(merged));
      broadcastTestResults(merged, lastMultiClientRef.current);
      return merged;
    } finally {
      if (panelRunRef.current === controller) panelRunRef.current = null;
      setIsRunningTests(false);
    }
  }, [runTestFiles, runPlaygroundTests, suspendPreview, resumePreview]);

  /** Cancels the run the panel started; an AI-requested run has no controller here, so it is never cancelled. */
  const cancelTests = useCallback(() => {
    panelRunRef.current?.abort();
  }, []);

  /**
   * Collect the suites/tests the spec files REGISTER, without running them.
   *
   * Powers the Tests panel's pre-run preview. We load each non-test source
   * (so a spec's relative imports resolve) and then each test file: loading a
   * file evaluates its top-level `describe`/`it` — and `defineSandboxTest`,
   * which lowers to `describe`/`it` — calls, which only REGISTER suite/test
   * names. The `it` bodies (which boot/drive the game) do NOT run until an
   * actual `runTests`, so this is cheap and side-effect-free. Reading the real
   * registrations is accurate where source-regex scanning is not: it sees the
   * labels even when a helper/factory produces the tests (e.g.
   * `tests: [movementTest('ArrowRight', …)]`), which a regex can't extract.
   */
  const collectFromFiles = useCallback(async (virtualFiles: Map<string, string>): Promise<CollectedTestSuite[]> => {
    const isTestFile = (p: string) => /\.(spec|test)\.(ts|tsx)$/.test(p);
    const testPaths = [...virtualFiles.keys()].filter(isTestFile);
    if (testPaths.length === 0) return [];

    // Load non-test source first so a spec's `import '../entities/Player'`
    // resolves during its (registration-only) evaluation.
    const moduleCache = new Map<string, any>();
    for (const [path] of virtualFiles) {
      if (!isTestFile(path)) {
        try { compileAndLoad(path, virtualFiles, moduleCache, testExternalModules, console); } catch { /* ignore */ }
      }
    }

    const collected: CollectedTestSuite[] = [];
    for (const testPath of testPaths) {
      resetTests();
      try {
        compileAndLoad(testPath, virtualFiles, new Map(moduleCache), testExternalModules, console);
        const file = testPath.replace(/^\//, '');
        for (const suite of collectRegisteredTests()) {
          collected.push({ name: suite.name, tests: suite.tests.map((t: { name: string }) => ({ name: t.name })), file });
        }
      } catch { /* a spec that fails to load just contributes nothing to the preview */ }
    }
    resetTests();
    return collected;
  }, [testExternalModules]);

  /** The suites and tests the editor's v1 view registers, the agent-file mirror overlaid (see `collectFromFiles`). */
  const collectTests = useCallback(async (systems: SandboxSystemsInput): Promise<CollectedTestSuite[]> => {
    const virtualFiles = new Map<string, string>();
    const addFolder = (prefix: string, folder: typeof systems.shared) => {
      folder.rootFiles.forEach(f => virtualFiles.set(`${prefix}/${f.name}.ts`, f.code));
      for (const [sub, files] of Object.entries(folder.subfolders)) {
        files.forEach(f => virtualFiles.set(`${prefix}/${sub}/${f.name}.ts`, f.code));
      }
    };
    addFolder('/shared', systems.shared);
    addFolder('/client', systems.client);
    addFolder('/server', systems.server);

    // Overlay the agent-file mirror. The worker's files are the ground truth
    // during a run — they WIN by path over the editor `systems` copy (so a
    // mid-fix edit is discovered, not the stale editor version), and they
    // contribute the flat `__tests__/*.spec.ts` specs the editor can't hold.
    for (const [p, code] of agentFilesRef.current) virtualFiles.set(p, code);

    return collectFromFiles(virtualFiles);
  }, [collectFromFiles]);

  /**
   * The project's tests for the Tests panel's pre-run listing: the spec suites its AI view registers (the AI's root
   * `__tests__/` included, the agent-file mirror overlaid, synthetic specs left out), then every playground its config
   * discovers that declares tests. A playground that fails to load contributes nothing here, as a spec that fails to
   * load does not — Run All reports both.
   */
  const collectProjectTests = useCallback(async (project: ProjectTree): Promise<CollectedTestSuite[]> => {
    const specs = await collectFromFiles(projectSpecFiles(project));
    const listing = await listProjectPlaygrounds(project.files, sandboxExternals());
    const playgrounds: CollectedTestSuite[] = listing.playgrounds.filter((playground) => playground.tests.length > 0).map((playground) => ({
      name: playground.name,
      file: playground.file,
      kind: 'playground',
      tests: playground.tests.map((test) => ({ name: test.label, id: test.id })),
      playground: {
        name: playground.name,
        ...(playground.member === undefined ? {} : { member: playground.member }),
        ...(playground.variant === undefined ? {} : { variant: playground.variant }),
      },
    }));
    return [...specs.map((suite) => ({ ...suite, kind: 'spec' as const })), ...playgrounds];
  }, [collectFromFiles]);

  const contextValue = useMemo(() => ({
    status,
    statusText,
    isLoading,
    clients,
    focusedClientId,
    environment: runtimeStateRef.current?.environment ?? null,
    testResults: shownTestResults,
    testProgress,
    isRunningTests,
    coverageReport,
    run,
    stop,
    addClient,
    restoreClients,
    removeClient,
    focusClient,
    resize,
    updateClientSettings,
    getClientSettings,
    runTests,
    runTestsFromFiles,
    collectTests,
    collectProjectTests,
    runAllTests,
    rerunTest,
    cancelTests,
    agentFilesSig,
    syncAgentFiles,
    replaceAgentFiles,
  }), [
    status,
    statusText,
    isLoading,
    clients,
    focusedClientId,
    shownTestResults,
    testProgress,
    isRunningTests,
    coverageReport,
    run,
    stop,
    addClient,
    restoreClients,
    removeClient,
    focusClient,
    resize,
    updateClientSettings,
    getClientSettings,
    runTests,
    runTestsFromFiles,
    collectTests,
    collectProjectTests,
    runAllTests,
    rerunTest,
    cancelTests,
    agentFilesSig,
    syncAgentFiles,
    replaceAgentFiles,
  ]);

  // Expose runner globally for testing
  useEffect(() => {
    (window as any).sandboxRunner = {
      run,
      stop,
      addClient,
      removeClient,
      focusClient,
      resize,
      updateClientSettings,
      getClientSettings,
      runTests,
      runTestsFromFiles,
      cancelTests,
      isRunning: () => status === 'running',
      getClientCount: () => clients.length,
      // Debug/e2e: read a live client's current input state ({up,down,left,
      // right} registered by the demo InputSystem). Used to verify multi-client
      // focus routing — only the FOCUSED client's input should flip on a key.
      getClientInput: (clientIndex: number): Record<string, boolean> | null => {
        try {
          const inst = clientsWithDOMRef.current[clientIndex]?.instance as any;
          return inst?.process?.$api?.input ?? null;
        } catch {
          return null;
        }
      },
    };
  }, [run, stop, addClient, removeClient, focusClient, resize, updateClientSettings, getClientSettings, runTests, runTestsFromFiles, cancelTests, status, clients.length]);

  return (
    <SandboxRunnerContext.Provider value={contextValue}>
      {children}
    </SandboxRunnerContext.Provider>
  );
}
